import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FeaturePanel from './FeaturePanel';
import { checkAuth } from '../utils/auth';
import FAQItem, { LoadingImage } from './FAQItem';

function LandingPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [returningUser, setReturningUser] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const tokenExists = !!token;
    setIsLoggedIn(tokenExists);
    if (tokenExists) {
      (async () => {
        const isActuallyLoggedIn = await checkAuth()
        setIsLoggedIn(isActuallyLoggedIn)
      })()
    }

    // Check if user has logged in before
    const returningUser = localStorage.getItem('returningUser');
    setReturningUser(returningUser === 'true');
  }, []);

  return (
    <main className="flex-grow container mx-auto px-4 py-8 relative overflow-hidden">
      {/* Decorative background orbs - kept to top portion to avoid FAQ expansion issues */}
      <div className="absolute top-10 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-60 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-[600px] left-[10%] w-72 h-72 bg-indigo-500/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-[800px] right-[10%] w-64 h-64 bg-purple-500/8 rounded-full blur-3xl pointer-events-none" />
      {(returningUser && !isLoggedIn) && (
      <div className="w-full max-w-sm mx-auto mb-6 p-3 bg-gradient-to-r from-gray-700/20 to-gray-800/20 rounded-md shadow-md">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-100 font-semibold">Welcome back!</p>
            <p className="text-gray-400 text-sm">We're glad to see you again.</p>
          </div>
          <Link 
            to="/auth?mode=login" 
            className="px-4 py-1.5 rounded-full bg-purple-700/80 text-white font-semibold hover:bg-purple-600 transition-colors duration-200 text-sm shadow-md hover:shadow-purple-500/30"
          >
            Log in
          </Link>
        </div>
      </div>
    )}

      <div className="max-w-3xl mx-auto text-center mb-12 pt-8 md:pt-12 relative">
        <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600
          animate-[fadeInUp_0.6s_ease-out]">
          Welcome to Receipts AI 
        </h1>
        <p className="mb-8 text-center text-xl text-gray-300 animate-[fadeInUp_0.6s_ease-out_0.1s_both]">
        Effortlessly manage receipts with AI extractions and gain insights into your spending habits.
        </p>

        <div className="space-y-4 animate-[fadeInUp_0.6s_ease-out_0.2s_both]">
          {isLoggedIn ? (
            <>
              <Link 
                to="/dashboard" 
                className="inline-block px-6 py-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold 
                  hover:from-blue-600 hover:to-purple-700 transition-all duration-300 
                  shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-purple-500/30 
                  hover:-translate-y-0.5 text-base"
              >
                Go to Dashboard
              </Link>
              <p className="text-sm text-gray-400">
                Thank you for being a valued customer!
              </p>
            </>
          ) : (
            <>
              <Link 
                to="/auth?mode=signup" 
                className="inline-block px-6 py-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold 
                  hover:from-blue-600 hover:to-purple-700 transition-all duration-300 
                  shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-purple-500/30 
                  hover:-translate-y-0.5 text-base"
              >
                Start Your Free 10-Day Trial
              </Link>
              <p className="text-sm text-gray-400">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600 font-semibold">$4.99/mo</span>
                <span className="text-gray-500 text-xs"> after free trial</span>
                <br />
                <span className="text-gray-500 text-xs">• Cancel anytime •</span>
              </p>
            </>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
        <FeaturePanel title="Upload Receipts" icon="📸" screenshot="/receipts2.gif">
          Easily upload your receipt photos or screenshots and let AI auto-extract all details (merchant, date, total, tax, tip, line item names/prices, discounts, etc).
        </FeaturePanel>
        
        <FeaturePanel title="Smart Categorization" icon="🧠" screenshot="/categories_2.png">
        AI automatically categorizes each line item, helping you understand where your money goes.
        </FeaturePanel>
        
        <FeaturePanel 
          title="Reports & Exports" 
          icon="📈" 
          screenshot="/dashboard.gif"
        >
          Get spending insights and export your data to CSV or PDF with images.
        </FeaturePanel>
      </div>

      {/* Additional Features Section */}
      <div className="text-center bg-gray-800/30 backdrop-blur-sm rounded-2xl p-8 md:p-12 mb-16 border border-gray-700/30">
        <h2 className="text-3xl font-bold mb-8 text-white">More Powerful Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5 max-w-4xl mx-auto text-left">
          <div>
            <ul className="space-y-5">
              <li className="flex items-start group">
                <span className="text-xl mr-3 bg-blue-500/20 border border-blue-500/30 p-2.5 rounded-xl flex items-center justify-center shrink-0 
                  group-hover:bg-blue-500/30 group-hover:scale-105 transition-all duration-200">📊</span>
                <div>
                  <span className="font-semibold text-white">Export to CSV & PDF</span>
                  <p className="text-gray-400 text-sm mt-0.5">Export receipt data to CSV for spreadsheets, or generate PDF reports with receipt images for expense reports and reimbursements.</p>
                </div>
              </li>
              <li className="flex items-start group">
                <span className="text-xl mr-3 bg-blue-500/20 border border-blue-500/30 p-2.5 rounded-xl flex items-center justify-center shrink-0
                  group-hover:bg-blue-500/30 group-hover:scale-105 transition-all duration-200">🗄️</span>
                <div>
                  <span className="font-semibold text-white">Digital Receipt Archive</span>
                  <p className="text-gray-400 text-sm mt-0.5">All your receipt images are securely stored and easily accessible for returns, warranty claims, expense records, and tax purposes.</p>
                </div>
              </li>
              <li className="flex items-start group">
                <span className="text-xl mr-3 bg-blue-500/20 border border-blue-500/30 p-2.5 rounded-xl flex items-center justify-center shrink-0
                  group-hover:bg-blue-500/30 group-hover:scale-105 transition-all duration-200">👥</span>
                <div>
                  <span className="font-semibold text-white">Split Receipts</span>
                  <p className="text-gray-400 text-sm mt-0.5">Easily split bills with friends by assigning items to different people and calculating each person's share.</p>
                </div>
              </li>
            </ul>
          </div>
          <div>
            <ul className="space-y-5">
              <li className="flex items-start group">
                <span className="text-xl mr-3 bg-purple-500/20 border border-purple-500/30 p-2.5 rounded-xl flex items-center justify-center shrink-0
                  group-hover:bg-purple-500/30 group-hover:scale-105 transition-all duration-200">📱</span>
                <div>
                  <span className="font-semibold text-white">Mobile Friendly</span>
                  <p className="text-gray-400 text-sm mt-0.5">Access, upload, and manage your receipts on any device with our responsive web application.</p>
                </div>
              </li>
              <li className="flex items-start group">
                <span className="text-xl mr-3 bg-purple-500/20 border border-purple-500/30 p-2.5 rounded-xl flex items-center justify-center shrink-0
                  group-hover:bg-purple-500/30 group-hover:scale-105 transition-all duration-200">✏️</span>
                <div>
                  <span className="font-semibold text-white">Fully Editable Data</span>
                  <p className="text-gray-400 text-sm mt-0.5">AI-extracted fields are editable, allowing you to correct or update any information as needed.</p>
                </div>
              </li>
              <li className="flex items-start group">
                <span className="text-xl mr-3 bg-purple-500/20 border border-purple-500/30 p-2.5 rounded-xl flex items-center justify-center shrink-0
                  group-hover:bg-purple-500/30 group-hover:scale-105 transition-all duration-200">🔍</span>
                <div>
                  <span className="font-semibold text-white">Smart Search</span>
                  <p className="text-gray-400 text-sm mt-0.5">Quickly find receipts by merchant or date, with more advanced search coming soon.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <p className="text-gray-500 text-sm mt-8">✨ We're constantly adding new features! Have suggestions? Let us know on <a href="https://forms.gle/JJoe8sS6mPFvRdgw9" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white underline transition-colors">our feedback form</a>.</p>
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto mb-16 relative">
        {/* Subtle glow behind FAQ */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />
        
        <h2 className="text-3xl font-bold text-center mb-12 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600 relative">
          Frequently Asked Questions
        </h2>
        <p className="text-center text-gray-400 text-base md:text-lg mb-8">
          Frequently asked questions with how-to instructions and <span className="text-purple-400 font-medium">screenshots</span> of our product features :)
        </p>
        <div className="space-y-4">
        <FAQItem 
            question="Can I create my own tags for better organization?"
            answer={
              <div>
                <p className="mb-3">
                  <strong>Yes!</strong> Our new tagging system helps you organize receipts exactly how you need:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-300">
                  <li><strong>Custom Tags:</strong> Create personal tags like "Business Travel", "Medical", "Client Dinner" to assign to receipt items</li>
                  <li><strong>System Tags:</strong> Pre-built tags for common use cases like "HSA/FSA Eligible" and "Charitable Donations"</li>
                  <li><strong>Export by Tags:</strong> Generate focused reports for tax preparation or expense analysis</li>
                  <li><strong>Coming Soon:</strong> AI will soon tentatively auto assign tags to items based on their description for you! Soon we will allow receipt level tags too.</li>
                </ul>
                <p className="mt-3 text-sm text-gray-400">
                  <strong>How to:</strong> Select any receipt → Click on items → Add tags in the Tags column. Create new tags on-the-fly!
                </p>
                <LoadingImage src="/tags_mobile.png" alt="Tags" className="w-1/2 mx-auto rounded-lg shadow-md my-4" />
              </div>
            }
          />

          <FAQItem 
            question="Can I export my data and generate PDF reports with receipt images?"
            answer={
              <div>
                <p className="mb-3">
                  <strong>Absolutely!</strong> Our Export Center offers powerful data export capabilities:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-300">
                  <li><strong>CSV Export:</strong> Export at receipt level or line-item level with full filtering—perfect for spreadsheets and accounting software</li>
                  <li><strong>PDF with Images:</strong> Generate professional reports with receipt thumbnails, line item tables, and full-size images in an appendix—ideal for expense reports and reimbursements</li>
                  <li><strong>Tag & Category Filtering:</strong> Export only items with specific tags or categories (great for tax prep!)</li>
                  <li><strong>Date Range Filtering:</strong> Perfect for quarterly or annual reports</li>
                  <li><strong>Individual Receipt Selection:</strong> Hand-pick specific receipts for targeted expense reports</li>
                </ul>
                <p className="mt-3 text-sm text-gray-400">
                  Access via Dashboard → Export Center. All exports include your AI-extracted or manually edited values (if any) for maximum accuracy.
                </p>
                <LoadingImage src="/export_center.png" alt="Export Center" className="w-full rounded-lg shadow-md my-4" />
              </div>
            }
          />
          
          <FAQItem 
            question="Can I edit the AI assigned categories and prices?"
            answer={
              <div>
                <p className="mb-3">
                  Almost <strong>everything is fully editable!</strong> We believe you should have complete control over your data:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-300">
                  <li><strong>Categories:</strong> Edit both high-level (Food & Dining) and detailed categories (Fast Food, Restaurants)</li>
                  <li><strong>Prices:</strong> Correct any AI extraction errors</li>
                  <li><strong>Item Names:</strong> Rename items for clarity</li>
                  <li><strong>Receipt Details:</strong> Edit merchant name, date, tax, and tip amounts</li>
                  <li><strong>Smart Tracking:</strong> Original AI values are preserved, edited values take precedence</li>
                </ul>
                <p className="mt-3 text-sm text-gray-400">
                  <strong>How to:</strong> Click the edit icon next to any field to edit in desktop. On mobile, click the receipt item to expand it and edit any field.
                </p>
              </div>
            }
          />
          
          <FAQItem 
            question="How does bill splitting work with friends?"
            answer={
              <div>
                <p className="mb-3">
                  <strong>Split bills effortlessly!</strong> Our <b>experimental</b> bill splitting feature makes group dining/shopping simple:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-300">
                  <li><strong>Item Assignment:</strong> Assign each line item to different people</li>
                  <li><strong>Multiple Splits:</strong> Create multiple groups (perfect for couples at group dinners)</li>
                  <li><strong>Tax & Tip Splitting:</strong> Optionally split tax and tip proportionally</li>
                  <li><strong>Visual Split Summary:</strong> Color-coded breakdown showing who owes what</li>
                  <li><strong>Venmo Integration:</strong> Export split amounts directly to Venmo for easy payment requests</li>
                </ul>
                <p className="mt-3 text-sm text-gray-400">
                  <strong>How to:</strong> Open any receipt → Click "Split" button → Assign items to people → Share screenshot or export to Venmo
                </p>
                <div className="flex gap-4 justify-center">
                  <LoadingImage src="/split_1.png" alt="Export Center" className="w-full rounded-lg shadow-md my-4" />
                  <LoadingImage src="/split_2.png" alt="Export Center" className="w-full rounded-lg shadow-md my-4" />
                </div>
              </div>
            }
          />


        <FAQItem 
            question="Can I use this on my phone and desktop?"
            answer={
              <div>
                <p className="mb-3">
                  <strong>Yes, Receipts AI works seamlessly across your devices!</strong>
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-300">
                  <li><strong>Mobile progressive web app:</strong> You'll be prompted to add to your home screen, and you can use it from your browser too! Upload right from your phone!</li>
                  <li><strong>Web Dashboard:</strong> Full-featured web interface for detailed analysis and management on desktop too--login from anywhere.</li>
                  <li>You can upload multiple receipts at once from any device.</li>
                  <li><strong>Responsive Design:</strong> Optimized experience on any screen size</li>
                </ul>
              </div>
            }
          />
          
          <FAQItem 
            question="What file formats can I upload?"
            answer={
              <div>
                <p className="mb-3">
                  <strong>We support all common receipt formats:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-300">
                  <li><strong>Images:</strong> JPEG, PNG, HEIC, HEIF (iPhone photos or screenshots work perfectly!)</li>
                  <li><strong>Documents:</strong> PDF receipts from online purchases or screenshots of emails</li>
                  <li><strong>Mobile Optimized:</strong> Take photos directly in-app or upload from gallery</li>
                  <li><strong>Bulk Upload:</strong> Select multiple files at once</li>
                </ul>
                <p className="mt-3 text-sm text-gray-400">
                  <strong>Tip:</strong> For best results, ensure receipts are well-lit and text is clearly readable.
                </p>
              </div>
            }
          />
        </div>
      </div>

      {/* Have More Questions Section */}
      <div className="text-center mb-16 relative">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-96 h-48 bg-blue-500/5 rounded-full blur-3xl" />
        </div>
        <div className="bg-gray-800/30 rounded-xl border border-gray-700/30 p-6 max-w-xl mx-auto relative backdrop-blur-sm">
          <h3 className="text-lg font-medium text-gray-200 mb-2">Have more questions?</h3>
          <p className="text-gray-400 text-sm mb-4">
            We'd love to hear from you! Ask us anything about features, pricing, or how Receipts AI can help your specific needs.
          </p>
          <a 
            href="https://forms.gle/JJoe8sS6mPFvRdgw9" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="inline-flex items-center px-4 py-2 rounded-md bg-gray-700/50 text-gray-300 text-sm font-medium hover:bg-gray-600/50 hover:text-white transition-all duration-200 border border-gray-600/30 hover:border-gray-500/50"
          >
            Ask us on our feedback form
          </a>
        </div>
      </div>

      <div className="text-center bg-gradient-to-r from-blue-900/40 to-purple-900/40 rounded-2xl p-12 relative overflow-hidden border border-blue-500/20">
        {/* Decorative glow */}
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
        
        <h2 className="text-3xl font-bold mb-4 relative">Ready to Get Started?</h2>
        <p className="text-xl text-gray-300 mb-8">
          Join our users who are saving time and getting detailed insights on their expenses.
        </p>
        {isLoggedIn ? (
          <>
            <Link 
              to="/dashboard" 
              className="inline-block px-8 py-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold hover:from-blue-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Go to Your Dashboard
            </Link>
            <p className="text-sm text-gray-400 mt-2">
              Thank you for choosing My Receipts AI!
            </p>
          </>
        ) : (
          <>
            <Link 
              to="/auth?mode=signup" 
              className="inline-block px-8 py-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold hover:from-blue-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Start Your Free Trial Now
            </Link>
            <p className="text-sm text-gray-400 mt-2">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600 font-semibold">$4.99/mo</span>
              <span className="text-gray-500 text-xs"> after free trial</span>
              <br />
              <span className="text-gray-500 text-xs">• Cancel anytime •</span>
            </p>
          </>
        )}
      </div>
    </main>
  );
}

export default LandingPage;